import Intercom, { boot, shutdown, trackEvent, update } from '@intercom/messenger-js-sdk';
import {
  AnalyticsAdapter,
  EventBase,
  mapAnalyticsDomainToInteractionName,
  UserVars,
} from '@lifeway/frontend-analytics';
import { logger } from '../../../utils/logger';
import { buildRoleFlags } from '../utils/buildRoleFlags';

const INTERCOM_AUTH_TIMEOUT_MS = 10000;

function fetchIntercomUserJwt(): Promise<string> {
  const intercomAuthUrl = String(window.config?.INTERCOM_AUTH_URL ?? '');
  if (!intercomAuthUrl) {
    return Promise.reject(new Error('Intercom auth URL is not configured.'));
  }

  // The user manager can throw on access, so the lookup is wrapped to keep this
  // a rejected promise rather than a synchronous throw into the caller.
  try {
    const userManager = window.getUserManager?.();
    if (!userManager) {
      return Promise.reject(new Error('User manager is not available.'));
    }

    return userManager.getUser().then(oidcUser => {
      if (!oidcUser?.access_token) {
        return Promise.reject(new Error('No valid access token found for user.'));
      }

      const controller = new AbortController();
      const timeoutId = window.setTimeout(() => controller.abort(), INTERCOM_AUTH_TIMEOUT_MS);

      return fetch(intercomAuthUrl, {
        method: 'GET',
        headers: { Authorization: `Bearer ${oidcUser.access_token}` },
        credentials: 'omit',
        signal: controller.signal,
      })
        .then(response => {
          if (!response.ok) {
            return Promise.reject(new Error(response.statusText));
          }
          return response.text();
        })
        .finally(() => {
          window.clearTimeout(timeoutId);
        });
    });
  } catch (error) {
    return Promise.reject(error);
  }
}

function asStringArray(value: unknown): string[] {
  return Array.isArray(value) ? (value as string[]) : [];
}

function buildIntercomSettings(userVars: UserVars): Record<string, unknown> {
  const allRoles = asStringArray(userVars.allRoles);
  const currentOrgRoles = asStringArray(userVars.currentOrgRoles);

  return {
    user_id: userVars.id,
    intercom_user_jwt: userVars.intercomJwt,
    email: userVars.email,
    name: userVars.fullName,
    userName: userVars.userName,
    currentOrgId: userVars.currentOrgId,
    currentOrgName: userVars.currentOrgName,
    gitHash: userVars.gitHash,
    url: userVars.url,
    orgSub: userVars.orgSub,
    curriculumSub: userVars.curriculumSub,
    personalSub: userVars.personalSub,
    ...buildRoleFlags(allRoles, currentOrgRoles, 'pascal'),
  };
}

type BootState = { kind: 'none' } | { kind: 'anonymous' } | { kind: 'user'; userId: string };

let bootState: BootState = { kind: 'none' };

function getIntercomAppId(): string {
  return (window.config?.INTERCOM_APP_ID as string) || '';
}

export function isIntercomEnabled(): boolean {
  return Boolean(getIntercomAppId());
}

function initIntercom(): void {
  const appId = getIntercomAppId();
  if (!appId) {
    return;
  }

  Intercom({ app_id: appId });
  bootState = { kind: 'anonymous' };
}

// Logging out shuts the messenger down, so anonymous visitors need it booted back
// up. Routing is hash based, so there is no page load to re-run the constructor.
function bootAnonymousIntercom(): void {
  const appId = getIntercomAppId();
  if (!appId || bootState.kind === 'anonymous') {
    return;
  }

  boot({ app_id: appId });
  bootState = { kind: 'anonymous' };
}

export function shutdownIntercom(): void {
  bootState = { kind: 'none' };
  shutdown();
}

export class IntercomAdapter implements AnalyticsAdapter {
  constructor() {
    initIntercom();
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public emit(...events: EventBase<any, any, any>[]): void {
    if (!isIntercomEnabled()) {
      return;
    }

    events.forEach(event => {
      const eventName = mapAnalyticsDomainToInteractionName(event.domain).replace(/ /g, '-').toLowerCase();
      trackEvent(eventName, {
        domain: event.domain,
        ...event.context,
        eventSubType: event.eventName,
      });

      // Support for former style of Intercom events in Ministry Grid
      switch (event.eventName) {
        case 'License Paired': {
          trackEvent('license_paired', {
            itemNumber: event.context.itemNumber,
          });

          const { brandCode, ageCategory } = event.context.curriculum ?? {};
          if (brandCode && ageCategory) {
            const pairedEvent = `${brandCode.toLowerCase()}_${ageCategory.toLowerCase()}_paired`;
            trackEvent(pairedEvent, { brandCode, ageCategory });
          }
          break;
        }
        case 'Trial Granted': {
          const { brandCode, ageCategory } = event.context.curriculum ?? {};
          if (brandCode && ageCategory) {
            const grantedEvent = `${brandCode.toLowerCase()}_${ageCategory.toLowerCase()}_trial_granted`;
            trackEvent(grantedEvent, { brandCode, ageCategory });
          }
          break;
        }
      }
    });
  }

  public anonymize(): Promise<void> {
    if (bootState.kind === 'user') {
      shutdownIntercom();
    }

    bootAnonymousIntercom();
    return Promise.resolve();
  }

  public identify(userVars: UserVars): Promise<void> {
    if (!isIntercomEnabled()) {
      return Promise.resolve();
    }

    return fetchIntercomUserJwt()
      .then(intercomJwt => {
        const settings = buildIntercomSettings({ ...userVars, intercomJwt });

        if (bootState.kind === 'user' && bootState.userId === userVars.id) {
          update(settings);
          return;
        }

        shutdown();
        boot({ app_id: getIntercomAppId(), ...settings });
        bootState = { kind: 'user', userId: userVars.id };
      })
      .catch(error => {
        logger.error('Failed to authenticate Intercom user:', error);
      });
  }

  public log(_level: number, _message: string): void {}
  public viewPage(_pageName: string, _pageProperties?: Record<string, unknown>): void {}
  public updatePage(_pageProperties?: Record<string, unknown>): void {}
  public unloadPage(): void {}
  public addSessionContext(_newContext: Record<string, unknown>): void {}
}
